import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GoogleLogo, MetaLogo, TiktokLogo, InstagramLogo, YoutubeLogo, SpotifyLogo, Copy, Sparkle, Rocket, UploadSimple, FloppyDisk, ArrowRight } from '@phosphor-icons/react';
import { toast as sonnerToast } from 'sonner';
import ContentPicker from '@/components/ContentPicker';
import AddSongDialog from '@/components/AddSongDialog';
import AddPlaylistDialog from '@/components/AddPlaylistDialog';

const PLATFORMS = [
  { id: 'google',    name: 'Google Ads',  icon: GoogleLogo,    color: 'text-blue-400' },
  { id: 'meta',      name: 'Meta Ads',    icon: MetaLogo,      color: 'text-blue-500' },
  { id: 'tiktok',    name: 'TikTok',      icon: TiktokLogo,    color: 'text-[#ccff00]' },
  { id: 'instagram', name: 'Instagram',   icon: InstagramLogo, color: 'text-pink-400' },
  { id: 'youtube',   name: 'YouTube',     icon: YoutubeLogo,   color: 'text-red-500' },
  { id: 'spotify',   name: 'Spotify',     icon: SpotifyLogo,   color: 'text-[#1DB954]' },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const AD_PLATFORMS = ['google', 'meta', 'tiktok'];

export default function CreativeStudio() {
  const nav = useNavigate();
  const [songs, setSongs] = useState([]);
  const [songId, setSongId] = useState('');
  const [playlists, setPlaylists] = useState([]);
  const [playlistId, setPlaylistId] = useState('');
  const [contentType, setContentType] = useState('song');
  const [showAddSong, setShowAddSong] = useState(false);
  const [showAddPlaylist, setShowAddPlaylist] = useState(false);
  const [tone, setTone] = useState('energetic');
  const [selected, setSelected] = useState(['google', 'meta', 'tiktok', 'instagram']);
  const [aiModel, setAiModel] = useState('gpt-terra');
  const [loading, setLoading] = useState(false);
  const [brief, setBrief] = useState(null);
  const [genImg, setGenImg] = useState(false);
  const [image, setImage] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [pricing, setPricing] = useState(null);

  const [mediaKind, setMediaKind] = useState('image');
  const [mediaModel, setMediaModel] = useState('gpt-image-1');
  const [mediaQuality, setMediaQuality] = useState('medium');
  const [mediaFormat, setMediaFormat] = useState('square');
  const [mediaDuration, setMediaDuration] = useState(4);
  const [klingDuration, setKlingDuration] = useState(5);

  const [savedCreativeId, setSavedCreativeId] = useState('');
  const [uploadingOwn, setUploadingOwn] = useState(false);
  const [savingCreative, setSavingCreative] = useState(false);
  const [ownMedia, setOwnMedia] = useState(null);
  const [useArtworkReference, setUseArtworkReference] = useState(true);
  const [creativeContext, setCreativeContext] = useState('');

  const loadContent = useCallback(async () => {
    const [songsRes, playlistsRes] = await Promise.all([
      api.get('/songs'),
      api.get('/playlists'),
    ]);

    const nextSongs = songsRes.data || [];
    const nextPlaylists = playlistsRes.data || [];

    setSongs(nextSongs);
    setPlaylists(nextPlaylists);

    if (nextSongs.length) {
      setSongId(prev => prev || nextSongs[0].song_id);
    }

    if (nextPlaylists.length) {
      setPlaylistId(prev => prev || nextPlaylists[0].playlist_id);
    }
  }, []);

  useEffect(() => {
    loadContent();
  }, [loadContent]);
  useEffect(() => {
    api.get('/campaigns/platform-config').then(r => setCfg(r.data));
    api.get('/pricing').then(r => setPricing(r.data.ai_pricing)).catch(() => {});
  }, []);

  const saveCreative = async (platformResult = null) => {
    if (!songId && contentType === 'song') {
      toast.error('Pick a song first');
      return null;
    }

    if (!playlistId && contentType === 'playlist') {
      toast.error('Pick a playlist first');
      return null;
    }

    setSavingCreative(true);

    try {
      const result = platformResult
        ? {
            platform: platformResult.platform,
            headline: platformResult.headline,
            primary_text: platformResult.primary_text,
            cta: platformResult.cta,
            hashtags: platformResult.hashtags || [],
            creative_brief: platformResult.creative_brief || '',
          }
        : null;

      const { data } = await api.post('/creative/save', {
        ...(contentType === 'song'
          ? { song_id: songId }
          : { playlist_id: playlistId }),
        name: ownMedia?.name || undefined,
        media_url: ownMedia?.url || image || undefined,
        media_type: ownMedia?.type || undefined,
        source: ownMedia ? 'upload' : 'ai',
        overall_angle: brief?.overall_angle || undefined,
        results: result ? [result] : (brief?.results || []),
      });

      setSavedCreativeId(data.creative_id);
      toast.success('Creative saved');
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save creative');
      return null;
    } finally {
      setSavingCreative(false);
    }
  };

  const goToCampaign = async (platformResult = null) => {
    let creativeId = savedCreativeId;

    if (!creativeId) {
      const saved = await saveCreative(platformResult);
      creativeId = saved?.creative_id;
    }

    if (!creativeId) return;

    nav(`/ads?creative_id=${encodeURIComponent(creativeId)}`);
  };

  const uploadOwnMaterial = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) {
      toast.error('Maximum creative file size is 100 MB');
      return;
    }

    setUploadingOwn(true);

    try {
      const fd = new FormData();
      fd.append('file', file);

      if (contentType === 'song') fd.append('song_id', songId);
      else fd.append('playlist_id', playlistId);

      const { data } = await api.post('/creative/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setOwnMedia({
        url: data.media_url,
        type: data.media_type,
        name: data.name,
      });

      setImage(null);
      setBrief(null);
      setSavedCreativeId(data.creative_id);

      toast.success('Your material is ready to use');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingOwn(false);
      event.target.value = '';
    }
  };

  const toggle = (p) => setSelected(s => s.includes(p) ? s.filter(x => x !== p) : [...s, p]);

  const generateBrief = async () => {
    if (contentType === 'song' && !songId) {
      toast.error('Pick a song');
      return;
    }

    if (contentType === 'playlist' && !playlistId) {
      toast.error('Pick a playlist');
      return;
    }
    if (selected.length === 0) { toast.error('Pick at least one platform'); return; }
    setLoading(true); setBrief(null); setImage(null);
    try {
      const { data } = await api.post('/creative/brief', {
        ...(contentType === 'song'
          ? { song_id: songId }
          : { playlist_id: playlistId }),
        platforms: selected,
        tone,
        model: aiModel,
      });
      setBrief(data);
      toast.success('Creative brief generated — copy is ready per platform');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  const generateMedia = async () => {
    const contentId = contentType === 'song' ? songId : playlistId;

    if (!contentId) {
      toast.error(contentType === 'song' ? 'Pick a song' : 'Pick a playlist');
      return;
    }

    setGenImg(true);

    try {
      let data;

      const common = contentType === 'song'
        ? {
            song_id: songId,
            creative_context: creativeContext,
            use_artwork_reference: useArtworkReference,
          }
        : {
            playlist_id: playlistId,
            creative_context: creativeContext,
            use_artwork_reference: useArtworkReference,
          };

      if (mediaKind === 'image') {
        const prompt = brief?.overall_angle
          ? `Ad key visual — ${brief.overall_angle}`
          : undefined;

        ({ data } = await api.post('/ai/image', {
          ...common,
          model: mediaModel,
          quality: mediaModel === 'gpt-image-1' ? mediaQuality : undefined,
          format: mediaFormat,
          prompt,
        }));
      } else {
        const duration =
          mediaModel === 'kling'
            ? klingDuration
            : mediaDuration;

        ({ data } = await api.post(
          mediaModel === 'kling' ? '/ai/video/kling' : '/ai/video',
          {
            ...common,
            model: mediaModel,
            duration,
            format: mediaFormat,
            prompt: brief?.overall_angle
            ? `Cinematic promotional video — ${brief.overall_angle}`
            : undefined,
          }
        ));
      }

      setImage(data.url);

      toast.success(
        `${mediaKind === 'image' ? 'Image' : 'Video'} ready · ${data.credits_charged} credits`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Media generation failed');
    } finally {
      setGenImg(false);
    }
  };


  const copyPack = (r) => {
    const t = `${r.headline}\n\n${r.primary_text}\n\nCTA: ${r.cta}\n\n${(r.hashtags || []).map(h => h.startsWith('#') ? h : '#' + h).join(' ')}`;
    navigator.clipboard.writeText(t);
    sonnerToast.success(`Copied ${r.platform}`);
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <header className="mb-8">
        <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Ad Creative Studio</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold mt-1">One brief. Every platform.</h1>
        <p className="text-zinc-400 mt-1 text-sm max-w-2xl">Generate ad copy, hashtags, key visual and creative direction for all your platforms in one shot — powered by AI.</p>
      </header>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Control panel */}
        <div className="lg:col-span-2 border border-zinc-800 rounded-xl bg-zinc-900/40 p-6 space-y-5 h-fit">
          <ContentPicker
            contentType={contentType}
            onContentTypeChange={(type) => {
              setContentType(type);
              setBrief(null);
              setImage(null);
            }}

            songs={songs}
            songId={songId}
            onSongChange={setSongId}

            playlists={playlists}
            playlistId={playlistId}
            onPlaylistChange={setPlaylistId}

            onAddSong={() => setShowAddSong(true)}
            onAddPlaylist={() => setShowAddPlaylist(true)}

            onRemoveSong={async (id) => {
              try {
                await api.delete(`/songs/${id}`);
                const { data } = await api.get('/songs');
                setSongs(data);

                if (songId === id) {
                  setSongId(data[0]?.song_id || '');
                }

                toast.success('Song removed');
              } catch (e) {
                toast.error(
                  e.response?.data?.detail || 'Could not remove song'
                );
              }
            }}

            onRemovePlaylist={async (id) => {
              try {
                await api.delete(`/playlists/${id}`);
                const { data } = await api.get('/playlists');
                setPlaylists(data);

                if (playlistId === id) {
                  setPlaylistId(data[0]?.playlist_id || '');
                }

                toast.success('Playlist removed');
              } catch (e) {
                toast.error(
                  e.response?.data?.detail || 'Could not remove playlist'
                );
              }
            }}

            songTestId="creative-song-select"
            playlistTestId="creative-playlist-select"
          />

          <AddSongDialog
            open={showAddSong}
            onOpenChange={setShowAddSong}
            onSaved={(newSong) => {
              setSongs(prev => [newSong, ...prev]);
              setSongId(newSong.song_id);
              setContentType('song');
              setBrief(null);
              setImage(null);
            }}
          />
          <AddPlaylistDialog
            open={showAddPlaylist}
            onOpenChange={setShowAddPlaylist}
            onSaved={(data) => {
              setPlaylists(prev => {
                const exists = prev.some(
                  p => p.playlist_id === data.playlist_id
                );

                return exists
                  ? prev.map(p =>
                      p.playlist_id === data.playlist_id ? data : p
                    )
                  : [data, ...prev];
              });

              setContentType('playlist');
              setPlaylistId(data.playlist_id);
              setBrief(null);
              setImage(null);
            }}
          />



          <div>
            <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">Platforms</label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {PLATFORMS.map(p => {
                const Icon = p.icon; const on = selected.includes(p.id);
                return (
                  <button key={p.id} data-testid={`creative-platform-${p.id}`} onClick={() => toggle(p.id)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-[background-color,border-color] ${
                      on ? 'border-[#ccff00] bg-[#ccff00]/10 text-zinc-100' : 'border-zinc-800 hover:border-zinc-600 text-zinc-400'
                    }`}>
                    <Icon size={16} className={p.color} weight="fill" /> {p.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">Tone</label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger data-testid="creative-tone-select" className="mt-2 bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="energetic">Energetic</SelectItem>
                  <SelectItem value="mysterious">Mysterious</SelectItem>
                  <SelectItem value="raw">Raw / underground</SelectItem>
                  <SelectItem value="dramatic">Dramatic</SelectItem>
                  <SelectItem value="playful">Playful</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">AI model</label>
              <Select value={aiModel} onValueChange={setAiModel}>
                <SelectTrigger data-testid="creative-model-select" className="mt-2 bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt-terra">GPT-4o</SelectItem>
                  <SelectItem value="gpt-mini">GPT-4o mini</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button data-testid="creative-generate-btn" onClick={generateBrief} disabled={loading} className="w-full btn-neon rounded-full font-display font-semibold h-11">
            <Sparkle size={16} weight="fill" className="mr-2" /> {loading ? 'Cooking…' : 'Generate creative brief'}
          </Button>

          {brief && (
            <>
              <div className="space-y-3 border-t border-zinc-800 pt-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useArtworkReference}
                    onChange={e => setUseArtworkReference(e.target.checked)}
                    className="mt-1 accent-[#ccff00]"
                  />
                  <span>
                    <span className="block text-sm text-zinc-200 font-medium">
                      Use existing artwork as reference
                    </span>
                    <span className="block text-xs text-zinc-500 mt-1 leading-relaxed">
                      Uses the artwork for mood, palette and visual identity,
                      but creates a new original composition.
                    </span>
                  </span>
                </label>

                <textarea
                  value={creativeContext}
                  onChange={e => setCreativeContext(e.target.value)}
                  placeholder="Optional context for AI — describe anything important about the song, artist or desired visual direction."
                  className="w-full min-h-[90px] rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none focus:border-[#ccff00]/50"
                />
              </div>

              <div className="space-y-3 border-t border-zinc-800 pt-4">

                <div>
                  <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">
                    Generate media
                  </label>

                  <Select
                    value={mediaKind}
                    onValueChange={(v) => {
                      setMediaKind(v);
                      setMediaModel(v === 'image' ? 'gpt-image-1' : 'sora');
                    }}
                  >
                    <SelectTrigger
                      data-testid="creative-media-kind-select"
                      className="mt-2 bg-zinc-950 border-zinc-800"
                    >
                      <SelectValue />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value="image">Photo</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {mediaKind === 'image' ? (
                  <>
                    <Select
                      value={mediaModel}
                      onValueChange={setMediaModel}
                    >
                      <SelectTrigger
                        data-testid="creative-img-model-select"
                        className="bg-zinc-950 border-zinc-800"
                      >
                        <SelectValue />
                      </SelectTrigger>

                      <SelectContent>
                        <SelectItem value="gpt-image-1">
                          GPT Image 1
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    {mediaModel === 'gpt-image-1' && (
                      <Select
                        value={mediaQuality}
                        onValueChange={setMediaQuality}
                      >
                        <SelectTrigger
                          data-testid="creative-img-quality-select"
                          className="bg-zinc-950 border-zinc-800"
                        >
                          <SelectValue />
                        </SelectTrigger>

                        <SelectContent>
                          <SelectItem value="medium">
                            Medium · {pricing?.gpt_image_1_medium_credits ?? 1} credit
                          </SelectItem>

                          <SelectItem value="high">
                            High detail · {pricing?.gpt_image_1_high_credits ?? 2} credits
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}

                    <div className="text-xs font-mono text-zinc-500">
                      Cost:{' '}
                      <span className="text-[#ccff00]">
                        {mediaQuality === 'high'
                          ? `${pricing?.gpt_image_1_high_credits ?? 2} credits`
                          : `${pricing?.gpt_image_1_medium_credits ?? 1} credit`
                        }
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <Select
                      value={mediaModel}
                      onValueChange={setMediaModel}
                    >
                      <SelectTrigger
                        data-testid="creative-vid-model-select"
                        className="bg-zinc-950 border-zinc-800"
                      >
                        <SelectValue />
                      </SelectTrigger>

                      <SelectContent>
                        <SelectItem value="sora">
                          Sora 2 · {pricing?.sora_2_credits_per_sec ?? 1} credits/sec
                        </SelectItem>

                        <SelectItem value="kling">
                          Kling 2.1 · {pricing?.kling_21_credits_per_sec ?? 3} credits/sec
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    {mediaModel === 'sora' ? (
                      <Select
                        value={String(mediaDuration)}
                        onValueChange={(v) => setMediaDuration(Number(v))}
                      >
                        <SelectTrigger
                          data-testid="creative-sora-duration-select"
                          className="bg-zinc-950 border-zinc-800"
                        >
                          <SelectValue />
                        </SelectTrigger>

                        <SelectContent>
                          <SelectItem value="4">
                            4s · {(pricing?.sora_2_credits_per_sec ?? 1) * 4} credits
                          </SelectItem>

                          <SelectItem value="8">
                            8s · {(pricing?.sora_2_credits_per_sec ?? 1) * 8} credits
                          </SelectItem>

                          <SelectItem value="12">
                            12s · {(pricing?.sora_2_credits_per_sec ?? 1) * 12} credits
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Select
                        value={String(klingDuration)}
                        onValueChange={(v) => setKlingDuration(Number(v))}
                      >
                        <SelectTrigger
                          data-testid="creative-kling-duration-select"
                          className="bg-zinc-950 border-zinc-800"
                        >
                          <SelectValue />
                        </SelectTrigger>

                        <SelectContent>
                          <SelectItem value="5">
                            5s · {(pricing?.kling_21_credits_per_sec ?? 3) * 5} credits
                          </SelectItem>

                          <SelectItem value="10">
                            10s · {(pricing?.kling_21_credits_per_sec ?? 3) * 10} credits
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}

                    <div className="text-xs font-mono text-zinc-500">
                      Cost:{' '}
                      <span className="text-[#ccff00]">
                        {mediaModel === 'sora'
                          ? `${(pricing?.sora_2_credits_per_sec ?? 1) * mediaDuration} credits`
                          : `${(pricing?.kling_21_credits_per_sec ?? 3) * klingDuration} credits`
                        }
                      </span>
                    </div>
                  </>
                )}

                <Button
                  data-testid="creative-generate-media-btn"
                  onClick={generateMedia}
                  disabled={genImg}
                  className="w-full btn-neon rounded-full font-display font-semibold h-11"
                >
                  {genImg
                    ? 'Generating…'
                    : `Generate ${mediaKind === 'image' ? 'photo' : 'video'}`
                  }
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Output */}
        <div className="lg:col-span-3 space-y-4">
          {!brief && !loading && (
            <div className="border border-dashed border-zinc-800 rounded-xl p-12 text-center bg-zinc-900/20 min-h-[400px] flex flex-col justify-center">
              <Sparkle size={32} weight="duotone" className="text-zinc-700 mx-auto" />
              <p className="mt-4 text-sm text-zinc-500 font-mono">Your per-platform creative brief will appear here</p>
            </div>
          )}
          {loading && (
            <div className="space-y-3 stagger-in">
              {[1, 2, 3, 4].map(i => <div key={i} className="h-40 rounded-xl bg-zinc-900/40 animate-pulse border border-zinc-800" />)}
            </div>
          )}
          {brief && (
            <>
              <div className="border border-[#ccff00]/30 rounded-xl bg-[#ccff00]/5 p-5">
                <div className="font-mono text-[10px] uppercase text-[#ccff00] tracking-wider">Overall angle</div>
                <div className="text-sm text-zinc-100 mt-2">{brief.overall_angle}</div>
              </div>
              {image && (
                <div className="border border-zinc-800 rounded-xl overflow-hidden">
                  <img src={`${BACKEND}${image}`} alt="key visual" data-testid="creative-image" className="w-full max-h-[420px] object-contain bg-zinc-950" />
                </div>
              )}
              {brief.results?.map((r, i) => {
                const P = PLATFORMS.find(p => p.id === r.platform.toLowerCase()) || PLATFORMS[0];
                const Icon = P.icon;
                return (
                  <div key={i} data-testid={`creative-result-${r.platform.toLowerCase()}`} className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Icon size={18} className={P.color} weight="fill" />
                        <span className="font-display">{P.name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {AD_PLATFORMS.includes(r.platform.toLowerCase()) && (
                          <div className="mt-5 pt-4 border-t border-zinc-800">
                            <Button
                              data-testid={`use-in-campaign-${r.platform.toLowerCase()}`}
                              onClick={() => goToCampaign(r)}
                              disabled={savingCreative}
                              className="w-full h-11 btn-neon rounded-full font-display font-semibold text-sm"
                            >
                              <ArrowRight size={16} weight="bold" className="mr-2" />
                              Use in Campaign
                            </Button>
                          </div>
                        )}
                        <button onClick={() => copyPack(r)} className="text-xs font-mono text-zinc-400 hover:text-[#ccff00] flex items-center gap-1"><Copy size={12} /> Copy pack</button>
                      </div>
                    </div>
                    <div className="font-display text-lg text-zinc-100">{r.headline}</div>
                    <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{r.primary_text}</p>
                    <div className="mt-3 flex items-center gap-3 text-xs">
                      <span className="font-mono text-zinc-500">CTA:</span>
                      <span className="text-[#ccff00] font-medium">{r.cta}</span>
                    </div>
                    {r.hashtags?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {r.hashtags.map((h, j) => (
                          <span key={j} className="text-xs font-mono text-[#ccff00] border border-[#ccff00]/30 px-2 py-0.5 rounded">{h.startsWith('#') ? h : `#${h}`}</span>
                        ))}
                      </div>
                    )}
                    {r.creative_brief && (
                      <div className="mt-4 pt-3 border-t border-zinc-800">
                        <div className="font-mono text-[10px] uppercase text-zinc-500">Creative direction</div>
                        <p className="text-sm text-zinc-400 mt-1">{r.creative_brief}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>


    </div>
  );
}
